import API from "./api";
import { VIDEO_LIMIT, PHOTO_LIMIT } from "./constants";
import config from "config";
import { v4 as uuidV4 } from "uuid";

const postFormDataWithProgress = (url, body, { headers, onProgress }) =>
  new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();

    request.open("POST", url);

    Object.entries(headers).forEach(([headerName, headerValue]) => {
      request.setRequestHeader(headerName, headerValue);
    });

    request.upload.addEventListener("progress", (event) => {
      onProgress(event.loaded);
    });

    request.addEventListener("load", () => {
      if (request.status < 200 || request.status >= 300) {
        reject(
          new Error(`Upload failed: ${request.status} ${request.statusText}`),
        );

        return;
      }

      try {
        resolve(JSON.parse(request.responseText));
      } catch (error) {
        reject(error);
      }
    });

    request.addEventListener("error", () => {
      reject(new Error("Network error during upload"));
    });

    request.addEventListener("abort", () => {
      reject(new Error("Upload aborted"));
    });

    request.send(body);
  });

export const hasAny = (obj, keys) => {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      return true;
    }
  }

  return false;
};

export const getImageFileType = (imageUrl) => {
  if (imageUrl) {
    const ext = imageUrl.split(".").pop().toLowerCase();

    if (ext === "png") {
      return "image/png";
    }
  }

  return "image/jpeg";
};

export const getImageUploadExt = (file) => {
  let ext;

  switch (file.type) {
    case "image/jpeg":
      ext = ".jpg";
      break;
    case "image/png":
      ext = ".png";
      break;
    case "image/heic":
    case "image/heif":
      ext = ".heic";
      break;
    case "image/webp":
      ext = ".webp";
      break;
    case "image/avif":
      ext = ".avif";
      break;
    default:
      throw new Error(
        "Only JPEG, PNG, HEIF, WebP, and AVIF images are supported.",
      );
  }

  return ext;
};

export const getImageUploadKey = (file) => {
  const ext = getImageUploadExt(file);

  return `${config.get("s3UploadKeyPrefix")}${uuidV4()}${ext}`;
};

export const fetchSignedS3Params = (params) => {
  // retrieves pre-signed parameters for uploading files to S3
  return API.get(
    "photos/presign_s3_upload/",
    {},
    params,
    ["*"], // Overrides casingIgnorePaths to ignore all
  ).then((data) => data);
};

export const fetchSignedCloudinaryParams = () => {
  return API.get(
    "videos/presign_cloudinary_upload/",
    {},
    {},
    ["*"], // Overrides casingIgnorePaths to ignore all
  ).then((data) => data);
};

export const fetchUpgradePrice = () => {
  const path = config.get("pricingEndpoint");

  return API.get(path).then((data) => parseInt(data, 10));
};

export const uploadToCloudinary = (
  key,
  file,
  siteId,
  progressCallback,
  videoCount = 0,
) => {
  if (file.size > 3_000_000_000) {
    return Promise.resolve({
      error: "VideoTooLarge",
      key,
      resourceName: "videos",
    });
  }
  if (videoCount >= VIDEO_LIMIT) {
    return Promise.resolve({
      error: "VideoLimitReached",
      key,
      resourceName: "videos",
    });
  }

  return fetchSignedCloudinaryParams()
    .then((params) => {
      const url = `https://api.cloudinary.com/v1_1/${config.get("cloudinaryCloudName")}/video/upload`;
      let currentLocation = 0;
      const bufferSize = 20_000_000; // 20 MB per chunk (cloudinary's default chunk size)
      const promises = [];
      const progress = {};

      while (currentLocation < file.size) {
        const chunk = file.slice(currentLocation, currentLocation + bufferSize);
        const chunkRange = [currentLocation, currentLocation + chunk.size - 1];
        const data = new FormData();

        data.append("file", chunk);
        data.append("api_key", config.get("cloudinaryApiKey"));
        data.append("timestamp", params.timestamp);
        data.append("signature", params.signature);
        data.append("folder", config.get("cloudinaryFolderName"));
        data.append("metadata", `site_id=${siteId}`);
        data.append("eager", "sp_full_hd_lean/m3u8|sp_full_hd_lean/mpd");
        data.append("eager_async", true);
        promises.push(
          postFormDataWithProgress(url, data, {
            headers: {
              "Content-Range": `bytes ${chunkRange[0]}-${chunkRange[1]}/${file.size}`,
              "X-Unique-Upload-Id": key,
            },
            onProgress: (loadedBytes) => {
              progress[chunkRange] = loadedBytes;
              progressCallback(
                key,
                Object.values(progress).reduce((prev, cur) => prev + cur, 0) /
                  file.size,
              );
            },
          }),
        );
        currentLocation += bufferSize;
      }

      return Promise.all(promises);
    })
    .then((responses) => {
      const finalResponse =
        responses.length === 1
          ? responses[0]
          : responses.find((response) => response.done);

      return {
        data: finalResponse,
        key,
        resourceName: "videos",
      };
    })
    .catch((error) => {
      console.error(error);

      return {
        error: "UnknownError",
        key,
        resourceName: "videos",
      };
    });
};

export const uploadToS3 = (
  file,
  key,
  process = true,
  resourceName = "",
  photoCount = 0,
) => {
  // accepts a File or Blob instance an uploads it to S3 using
  // pre-signed form parameters

  // Include additional precision for tiebreaker
  const microseconds = parseInt((window.performance.now() % 1) * 1000);
  const createdAt = new Date().toISOString().slice(0, -1) + microseconds + "Z";

  if (file.size > config.get("s3UploadMaxSizeBytes")) {
    return Promise.resolve({
      error: "ImageTooLarge",
      key,
      resourceName: "photos",
    });
  }
  if (photoCount >= PHOTO_LIMIT) {
    return Promise.resolve({
      error: "PhotoLimitReached",
      key,
      resourceName: "photos",
    });
  }

  if (file.size === 0) {
    console.error("File failed to upload due to being 0 bytes");

    return Promise.resolve({
      error: "ImageIsEmpty",
      key,
      resourceName: "photos",
    });
  }
  const ext = getImageUploadExt(file);

  return new Promise((resolve, reject) => {
    fetchSignedS3Params({
      ext,
      key: key || "",
      mode: process ? "process" : "",
    })
      .then((params) => {
        const data = new window.FormData();

        Object.keys(params.fields).forEach((key) => {
          data.append(key, params.fields[key]);
        });
        data.append("file", file);
        fetch(params.url, {
          body: data,
          method: "POST",
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(
                `S3 upload failed: ${response.status} ${response.statusText}`,
              );
            }

            return response.text();
          })
          .then((responseText) => {
            const document = new window.DOMParser().parseFromString(
              responseText,
              "text/xml",
            );
            const error = document.getElementsByTagName("Error");

            if (error.length > 0) {
              reject(
                new Error(
                  "Images must be JPEG, PNG, WebP, or HEIC, and less than 20MB.",
                ),
              );

              return;
            }

            const keyElement = document.getElementsByTagName("Key")[0];
            const uploadedKey = keyElement ? keyElement.textContent : "";

            // Build the URL from the CDN rather than the Location element S3
            // returns, which points at the bucket's own domain. Callers put
            // this straight into render state, so a bucket URL would be the
            // one image read that bypasses CloudFront (and its WAF and edge
            // cache) until the API round-trip normalizes the stored path.
            // Keys are `<prefix>/<uuid><ext>`, so they need no escaping.
            resolve({
              createdAt,
              data: document,
              key: uploadedKey,
              resourceName,
              url: uploadedKey
                ? `${config.get("photoStorageUrl")}/${uploadedKey}`
                : "",
            });
          })
          .catch((error) => {
            console.error(error);

            resolve({
              error: "UnknownError",
              key,
              resourceName,
            });
          });
      })
      .catch((error) => {
        console.error(error);
        reject(new Error("Network error retrieving upload parameters.", error));
      });
  });
};

export const preventDefault = (next) => (event) => {
  if (event) {
    event.preventDefault();
  }

  next(event);
};

export const getNewPosition = (collection) => {
  /* Returns max position + 1 based on the current collection items max
     position.
  */
  if (!collection) {
    return 1;
  }
  const positions = collection.items.map((item) => item.data.position);

  return Math.max(...positions, 0) + 1;
};

export const getGuestTokenCookie = () =>
  ("; " + document.cookie)
    .split(`; ${config.get("guestTokenCookieName")}=`)
    .pop()
    .split(";")[0];

export const getGuestAuthSettings = () => {
  const headers = {};
  const guestToken = getGuestTokenCookie();

  // Some endpoints require guest auth, and we want to conditionally
  // append the guest token if it exists
  if (guestToken) {
    headers["Authorization"] = `Guest ${guestToken}`;
  }

  return { headers };
};

export const capitalizeEachWord = (str) => {
  const words = str.toLowerCase().split(" ");

  // capitalize the first letter of each word
  const capitalizedWords = words.map((word) => {
    if (word.length === 0) {
      return ""; // handle empty strings
    }

    return word.charAt(0).toUpperCase() + word.substring(1);
  });

  return capitalizedWords.join(" ");
};
